import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User } from 'lucide-react';
import Button from '../ui/button';
import AuthModal from '../auth/auth-modal';
import HubSpotModal from '../ui/hubspot-modal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../AuthProvider';

const navItems = [
  {
    label: 'Entreprise',
    path: '/entreprise/recrutement',
    children: [
      { label: 'Vos recrutements', path: '/entreprise/recrutement' },
      { label: 'Campagnes de recrutement', path: '/entreprise/campagnes' },
      { label: 'Externalisation RH', path: '/entreprise/externalisation' },
      { label: 'Outils & IA RH', path: '/entreprise/ia' },
      { label: 'Assistant TalentHrAi', path: '/entreprise/talent-rh-ai' },
      
    ],
  },
  {
    label: 'Candidat',
    path: '/candidat/offres',
    children: [
      { label: 'Nos meilleures offres', path: '/candidat/offres' },
      { label: 'Pourquoi recruteurs ?', path: '/candidat/pourquoi' },
      { label: 'Programme de cooptation', path: 'https://loocatme.referrals.co/', external: true },
      { label: 'Coach Talent AI', path: '/candidat/talentai' },
    ],
  },
  {
    label: 'Ressources',
    path: '/ressources/articles',
    children: [
      { label: 'Les articles RH', path: '/ressources/articles' },
      { label: 'Les outils', path: '/ressources/outils' },
      { label: 'Mapping RH', path: '/ressources/mapping-rh' },
      { label: 'Ressources téléchargeables', path: '/ressources/ressources' },
    ],
  },
  {
    label: 'Infos',
    path: '/infos/questions',
    children: [
      { label: 'Les questions fréquentes', path: '/infos/questions' },
      { label: 'La charte du recrutement', path: '/infos/charte' },
      { label: 'Nos tarifs', path: '/infos/tarifs' },
      { label: 'Contact', path: '/infos/contact' },
    ],
  },
];

const adminItems = {
  label: 'Admin',
  path: '/admin',
  children: [
    { label: 'Timeline Recrutement', path: '/admin/timeline-recrutement' },
    { label: 'Timeline Campagnes', path: '/admin/timeline-campagnes' },
    { label: 'Forecasts', path: '/admin/forecasts' },
    { label: 'Dashboard Client', path: '/client/dashboard' },
  ],
};

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'logo')
          .maybeSingle();
        if (!error && data?.value) setLogo(data.value);
        else setLogoError(true);
      } catch (error) {
        setLogoError(true);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) return setIsAdmin(false);
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (error || !data) {
        console.error('Error fetching user role:', error);
        return setIsAdmin(false);
      }

      setIsAdmin(data.role === 'admin');
    };

    checkAdmin();
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/?logout=' + Date.now();
    } catch (error) {
      window.location.href = '/?force_logout=' + Date.now();
    }
  };

  const handleNavigation = (path: string, external?: boolean) => {
    if (external) window.open(path, '_blank');
    else window.location.href = path;
  };

  const allNavItems = isAdmin ? [...navItems, adminItems] : navItems;

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur border-b border-neutral-200 py-2' : 'bg-transparent py-4'}`}>
      <div className="container-custom flex items-center justify-between">
        <Link to="/" className="flex items-center">
          {!logoError && logo ? (
            <img src={logo} alt="recruteurs.io" className="h-12 object-contain" />
          ) : (
            <span className="text-xl font-title font-bold text-left gradient-text">recruteurs.io</span>
          )}
        </Link>

        <nav className="hidden md:flex items-center space-x-8">
          {allNavItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to={item.path}
                className={`relative group font-medium transition-colors text-neutral-800 hover:text-[#d03484] ${location.pathname.startsWith(item.path) ? 'text-[#d03484]' : ''}`}
              >
                {item.label}
                <span className="absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-[#d03484] to-[#d47254] transition-all duration-300" />
              </Link>
              <AnimatePresence>
                {item.children && activeDropdown === item.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-medium overflow-hidden z-50"
                  >
                    <div className="py-2">
                      {item.children.map((child) => (
                        <div
                          key={child.label}
                          onClick={() => handleNavigation(child.path, child.external)}
                          className={`block px-4 py-2 hover:bg-neutral-50 transition-all duration-200 hover:translate-x-1 cursor-pointer ${location.pathname === child.path ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] font-medium' : 'text-neutral-700 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#d03484] hover:to-[#d47254]'}`}
                        >
                          {child.label}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-neutral-700">
                  <User size={18} />
                  <span className="text-sm">{user.email}</span>
                </div>
                <Button variant="outline" onClick={handleLogout} className="text-neutral-900 hover:text-[#d03484]">
                  Déconnexion
                </Button>
              </div>
            ) : (
              <>
                <Button variant="outline" size="sm" href="https://calendar.app.google/GudGybfrFPWVuk146">
                  📅 Planifier un appel
                </Button>
                <Button variant="outline" size="sm" onClick={() => setIsAuthModalOpen(true)}>
                  <span className="text-neutral-900">⚡</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] ml-1">
                    login
                  </span>
                </Button>
              </>
            )}
          </div>
        </nav>

        <button className="md:hidden text-neutral-800 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-neutral-100"
          >
            <div className="px-6 py-4 space-y-4">
              {allNavItems.map((item) => (
                <div key={item.label}>
                  <p className="font-bold text-neutral-800">{item.label}</p>
                  <div className="ml-4 mt-2 space-y-1">
                    {item.children?.map((child) => (
                      child.external ? (
                        <a key={child.label} href={child.path} target="_blank" rel="noopener noreferrer" className="block text-neutral-700 hover:text-primary" onClick={() => setIsOpen(false)}>
                          {child.label}
                        </a>
                      ) : (
                        <Link key={child.label} to={child.path} className="block text-neutral-700 hover:text-primary" onClick={() => setIsOpen(false)}>
                          {child.label}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t border-neutral-100 space-y-2">
                {user ? (
                  <>
                    <div className="text-sm text-neutral-600">{user.email}</div>
                    <Button variant="gradient" fullWidth onClick={handleLogout}>
                      Déconnexion
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" fullWidth href="https://calendar.app.google/GudGybfrFPWVuk146">
                      📅 Planifier un appel
                    </Button>
                    <Button variant="outline" fullWidth onClick={() => setIsAuthModalOpen(true)} className="text-center bg-gradient-to-r from-[#d03484] to-[#d47254] text-transparent bg-clip-text">
                      login
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </header>
  );
}